import { cva, type VariantProps } from "class-variance-authority";
import { type FC, type HTMLAttributes, type Ref } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";
import { Icon, type IconProps, type IconSize } from "./Icon";

/**
 * Tone → surface + glyph color. The glyph paints with `currentColor`, so the tone sets both
 * the tile background and the icon color in one place.
 */
const toneClasses = {
	primary: "bg-brand-primary text-inverse",
	accent: "bg-brand-accent text-inverse",
	neutral: "bg-surface-neutral text-secondary",
	subtle: "bg-surface-light text-tertiary",
};

export type IconTileTone = keyof typeof toneClasses;
export const ICON_TILE_TONES = Object.keys(toneClasses) as readonly IconTileTone[];

const sizeClasses = {
	sm: "h-[22px] w-[22px] rounded-[4px]",
	md: "h-[28px] w-[28px] rounded-[5px]",
	lg: "h-[34px] w-[34px] rounded-md",
};

export type IconTileSize = keyof typeof sizeClasses;
export const ICON_TILE_SIZES = Object.keys(sizeClasses) as readonly IconTileSize[];

/** Glyph size per tile size, so the icon always sits on the same optical inset. */
const glyphSize = {
	sm: "micro",
	md: "mini",
	lg: "small",
} as const satisfies Record<IconTileSize, IconSize>;

const iconTileVariants = cva("inline-flex shrink-0 items-center justify-center", {
	variants: {
		tone: toneClasses,
		size: sizeClasses,
	},
	defaultVariants: { tone: "primary", size: "md" },
});

type IconTileVariantProps = VariantProps<typeof iconTileVariants>;

export type IconTileProps = Omit<HTMLAttributes<HTMLDivElement>, "color" | "children"> & {
	/** Which glyph to render. Passed straight through to the inner `Icon`. */
	type: IconProps["type"];

	/**
	 * Surface + glyph color of the tile.
	 * @default "primary"
	 */
	tone?: IconTileVariantProps["tone"];

	/**
	 * Tile dimension, which also picks the glyph size (`sm` 22px/micro, `md` 28px/mini,
	 * `lg` 34px/small).
	 * @default "md"
	 */
	size?: IconTileVariantProps["size"];

	/** Render the solid glyph instead of the outline glyph. Passed through to the inner `Icon`. */
	filled?: boolean;

	/** Analytics identifier. Expands to `data-pendo-id` + `data-amp-track-id`. */
	trackingId?: string;

	ref?: Ref<HTMLDivElement>;
};

/**
 * A single glyph on its own small, rounded surface — the identity mark for a non-human actor
 * or a content kind, at a size that sits inline with `body` text.
 *
 * Reach for `Avatar` when the mark stands for a person (a circle with their initials), for
 * `Icon` when the glyph needs no surface of its own, and for `ClickableIcon` when the glyph is
 * the affordance rather than the label. The tile is decorative by default: give it an
 * `aria-label` (or leave it beside real text) when it carries meaning on its own.
 */
export const IconTile: FC<IconTileProps> = ({
	type,
	tone,
	size,
	filled,
	trackingId,
	className,
	ref,
	...rest
}) => (
	<div
		ref={ref}
		className={cn(iconTileVariants({ tone, size }), className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	>
		<Icon aria-hidden filled={filled} size={glyphSize[size ?? "md"]} type={type} />
	</div>
);
IconTile.displayName = "IconTile";
